import { Component } from 'react'
import type { ErrorInfo, ReactNode } from 'react'

type Props = { children: ReactNode; fallback?: ReactNode }
type State = { error: Error | null }

/**
 * Top-level error boundary. React error boundaries must be class components;
 * this catches render-time crashes so the whole app doesn't white-screen.
 * Wire componentDidCatch to a real reporter (Sentry, etc.) later.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('Uncaught error:', error, info)
  }

  render() {
    if (this.state.error) {
      return (
        this.props.fallback ?? (
          <div className="p-6 space-y-2">
            <h1 className="text-lg font-bold">Something went wrong</h1>
            <p className="text-sm text-gray-600">{this.state.error.message}</p>
          </div>
        )
      )
    }
    return this.props.children
  }
}
